import React, { useState, useEffect, useCallback } from 'react';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
    Send, RefreshCw, Clock, CheckCircle, XCircle, AlertTriangle,
    Zap, Eye, Trash2, Settings, ChevronDown, ChevronUp, Copy, Loader2, ImageIcon
} from 'lucide-react';

const PLATFORM_META = {
    telegram: { label: 'Telegram', color: '#0088CC', icon: 'TG' },
    twitter_x: { label: 'X (Twitter)', color: '#1DA1F2', icon: 'X' },
    youtube: { label: 'YouTube', color: '#FF0000', icon: 'YT' },
    instagram: { label: 'Instagram', color: '#E4405F', icon: 'IG' },
    facebook: { label: 'Facebook', color: '#1877F2', icon: 'FB' },
};

function PlatformBadge({ platform, size = 'md' }) {
    const meta = PLATFORM_META[platform] || { label: platform, color: '#666', icon: '?' };
    const s = size === 'sm' ? 'w-7 h-7 text-[10px]' : 'w-9 h-9 text-xs';
    return (
        <div className={`${s} rounded-lg flex items-center justify-center font-bold`}
            style={{ backgroundColor: `${meta.color}20`, color: meta.color }}>
            {meta.icon}
        </div>
    );
}

function StatusCard({ platform, connected, fieldsRequired, fieldsConfigured }) {
    const meta = PLATFORM_META[platform] || { label: platform, color: '#666' };
    return (
        <div className={`glass-card p-4 border ${connected ? 'border-green-500/20' : 'border-white/5'}`}
            data-testid={`status-${platform}`}>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <PlatformBadge platform={platform} />
                    <div>
                        <div className="text-sm font-bold text-white">{meta.label}</div>
                        <div className="text-[10px] text-white/30 tech-mono">
                            {connected ? 'CONNECTED' : `${fieldsConfigured?.length || 0}/${fieldsRequired?.length || 0} FIELDS`}
                        </div>
                    </div>
                </div>
                {connected ? (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                ) : (
                    <XCircle className="w-5 h-5 text-white/20" />
                )}
            </div>
        </div>
    );
}

function ContentEditor({ platform, content, onChange, onCopy }) {
    const meta = PLATFORM_META[platform] || { label: platform, color: '#666' };
    const charLimit = platform === 'twitter_x' ? 280 : 4000;
    const isOver = content.length > charLimit;

    return (
        <div className="glass-card p-4 border border-white/5" data-testid={`content-${platform}`}>
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <PlatformBadge platform={platform} size="sm" />
                    <span className="text-sm font-bold text-white">{meta.label}</span>
                </div>
                <div className="flex items-center gap-2">
                    <span className={`text-[10px] tech-mono ${isOver ? 'text-red-400' : 'text-white/30'}`}>
                        {content.length}/{charLimit}
                    </span>
                    <button onClick={() => onCopy(content)} className="text-white/30 hover:text-white transition-colors"
                        title="Copy content">
                        <Copy className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>
            <textarea
                value={content}
                onChange={e => onChange(platform, e.target.value)}
                className="w-full bg-black/40 border border-white/10 rounded-lg p-3 text-sm text-white/80 resize-none focus:border-cyan-500/30 focus:outline-none"
                rows={platform === 'twitter_x' ? 3 : 5}
                data-testid={`textarea-${platform}`}
            />
        </div>
    );
}

function PostHistoryItem({ post, onDelete }) {
    const meta = PLATFORM_META[post.platform] || { label: post.platform, color: '#666' };
    const success = post.result?.success;
    const time = post.posted_at ? new Date(post.posted_at).toLocaleString() : 'Unknown';

    return (
        <div className="glass-card p-4 border border-white/5" data-testid={`history-${post.id}`}>
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <PlatformBadge platform={post.platform} size="sm" />
                    <span className="text-sm font-bold text-white">{meta.label}</span>
                    {success ? (
                        <span className="text-[10px] px-2 py-0.5 rounded bg-green-500/20 text-green-400 tech-mono">SENT</span>
                    ) : (
                        <span className="text-[10px] px-2 py-0.5 rounded bg-red-500/20 text-red-400 tech-mono">FAILED</span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-[10px] text-white/20 tech-mono">{time}</span>
                    <button onClick={() => onDelete(post.id)} className="text-white/20 hover:text-red-400 transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>
            <div className="text-xs text-white/50 line-clamp-2">{post.content}</div>
            {!success && post.result?.error && (
                <div className="text-[10px] text-red-400/60 mt-1">{post.result.error}</div>
            )}
            {post.match_info?.team_a && (
                <div className="text-[10px] text-cyan-400/40 mt-1 tech-mono">
                    {post.match_info.team_a} vs {post.match_info.team_b}
                </div>
            )}
        </div>
    );
}

export default function SocialMediaHub() {
    const [tab, setTab] = useState('compose');
    const [platforms, setPlatforms] = useState([]);
    const [history, setHistory] = useState([]);
    const [settings, setSettings] = useState({ enabled: false, platforms: [], auto_generate: true });
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [posting, setPosting] = useState(false);
    const [teams, setTeams] = useState([]);

    // Compose state
    const [teamA, setTeamA] = useState('');
    const [teamB, setTeamB] = useState('');
    const [venue, setVenue] = useState('');
    const [winPctA, setWinPctA] = useState('55');
    const [winPctB, setWinPctB] = useState('45');
    const [reasoning, setReasoning] = useState('');
    const [godModeVerdict, setGodModeVerdict] = useState('');
    const [contentMap, setContentMap] = useState({});
    const [selectedPlatforms, setSelectedPlatforms] = useState([]);
    const [postResults, setPostResults] = useState(null);
    const [generatingImage, setGeneratingImage] = useState(false);
    const [generatedImage, setGeneratedImage] = useState(null);

    // Settings state
    const [settingsExpanded, setSettingsExpanded] = useState(false);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const [statusRes, historyRes, settingsRes, teamsRes] = await Promise.all([
                API.get('/social/status'),
                API.get('/social/history'),
                API.get('/social/settings'),
                API.get('/teams'),
            ]);
            setPlatforms(statusRes.data.platforms || []);
            setHistory(historyRes.data.posts || []);
            setSettings(settingsRes.data || { enabled: false, platforms: [], auto_generate: true });

            const teamList = teamsRes.data.teams || teamsRes.data || [];
            setTeams(teamList.map(t => t.short_name || t.name || t.id).filter(Boolean));
        } catch (err) {
            console.error('Failed to load social data', err);
        }
        setLoading(false);
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const handleGenerate = async () => {
        if (!teamA || !teamB) return;
        setGenerating(true);
        setPostResults(null);
        try {
            const res = await API.post('/social/generate-all', {
                match_info: { team_a: teamA, team_b: teamB, venue },
                prediction: {
                    win_pct_a: parseFloat(winPctA) || 50,
                    win_pct_b: parseFloat(winPctB) || 50,
                    reasoning,
                    god_mode_verdict: godModeVerdict,
                },
            });
            setContentMap(res.data.contents || {});
            // Auto-select connected platforms
            const connected = platforms.filter(p => p.connected).map(p => p.platform);
            setSelectedPlatforms(connected);
        } catch (err) {
            console.error('Generate failed', err);
        }
        setGenerating(false);
    };

    const handleGenerateImage = async () => {
        if (!teamA || !teamB) return;
        setGeneratingImage(true);
        setGeneratedImage(null);
        try {
            const res = await API.post('/social/generate-image', {
                team_a: teamA, team_b: teamB,
                win_pct_a: parseFloat(winPctA) || 50,
                win_pct_b: parseFloat(winPctB) || 50,
                venue, reasoning,
            });
            if (res.data.success) {
                setGeneratedImage(res.data.image_base64);
            }
        } catch (err) { console.error('Image generation failed', err); }
        setGeneratingImage(false);
    };

    const handlePost = async () => {
        if (selectedPlatforms.length === 0) return;
        setPosting(true);
        try {
            const res = await API.post('/social/post', {
                platforms: selectedPlatforms,
                content_map: contentMap,
                match_info: { team_a: teamA, team_b: teamB, venue },
            });
            setPostResults(res.data.results || {});
            // Refresh history
            const histRes = await API.get('/social/history');
            setHistory(histRes.data.posts || []);
        } catch (err) {
            console.error('Post failed', err);
        }
        setPosting(false);
    };

    const handleDeleteHistory = async (id) => {
        try {
            await API.delete(`/social/history/${id}`);
            setHistory(prev => prev.filter(p => p.id !== id));
        } catch (err) {
            console.error('Delete failed', err);
        }
    };

    const [savingSettings, setSavingSettings] = useState(false);

    const handleSaveSettings = async () => {
        setSavingSettings(true);
        try {
            await API.put('/social/settings', settings);
            toast.success('Settings saved successfully');
        } catch (err) {
            console.error('Save settings failed', err);
            toast.error(err.response?.data?.detail || 'Failed to save settings');
        }
        setSavingSettings(false);
    };

    const handleContentChange = (platform, value) => {
        setContentMap(prev => ({ ...prev, [platform]: value }));
    };

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text).catch(() => {});
    };

    const togglePlatform = (platform) => {
        setSelectedPlatforms(prev =>
            prev.includes(platform) ? prev.filter(p => p !== platform) : [...prev, platform]
        );
    };

    const connectedCount = platforms.filter(p => p.connected).length;

    if (loading) {
        return (
            <div className="max-w-6xl mx-auto p-6">
                <div className="glass-card p-12 text-center">
                    <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-5" data-testid="social-media-hub">
            {/* Header */}
            <div className="glass-card p-5 border-l-4 border-l-cyan-500">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Send className="w-5 h-5 text-cyan-400" />
                        <div>
                            <h1 className="heading-primary text-base md:text-xl text-white">SOCIAL MEDIA COMMAND CENTER</h1>
                            <div className="text-xs text-white/30 mt-0.5">
                                AI-powered content generation and multi-platform posting
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className={`text-[10px] px-2 py-1 rounded tech-mono ${connectedCount > 0 ? 'bg-green-500/20 text-green-400' : 'bg-white/5 text-white/30'}`}>
                            {connectedCount}/{platforms.length} CONNECTED
                        </span>
                    </div>
                </div>
            </div>

            {/* Platform Status Row */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {platforms.map(p => (
                    <StatusCard key={p.platform} {...p} />
                ))}
            </div>

            {/* Tab Navigation */}
            <div className="flex gap-1 bg-black/30 rounded-lg p-1 border border-white/5">
                {[
                    { id: 'compose', label: 'Compose & Post', icon: Send },
                    { id: 'history', label: 'Post History', icon: Clock },
                    { id: 'settings', label: 'Settings', icon: Settings },
                ].map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-md text-xs font-medium transition-all flex-1 justify-center ${tab === t.id ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20' : 'text-white/40 hover:text-white/60'}`}
                        data-testid={`tab-${t.id}`}>
                        <t.icon className="w-3.5 h-3.5" />
                        {t.label}
                    </button>
                ))}
            </div>

            {/* COMPOSE TAB */}
            {tab === 'compose' && (
                <div className="space-y-5">
                    {/* Match Input */}
                    <div className="glass-card p-5 border border-white/5">
                        <h2 className="heading-primary text-sm text-white mb-4">MATCH DETAILS</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                                <label className="text-[10px] text-white/30 tech-mono mb-1 block">TEAM A</label>
                                {teams.length > 0 ? (
                                    <select value={teamA} onChange={e => setTeamA(e.target.value)}
                                        className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-500/30 focus:outline-none"
                                        data-testid="select-team-a">
                                        <option value="">Select team...</option>
                                        {teams.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                ) : (
                                    <Input value={teamA} onChange={e => setTeamA(e.target.value)}
                                        placeholder="e.g. CSK" className="bg-black/50 border-white/10 text-white" data-testid="input-team-a" />
                                )}
                            </div>
                            <div>
                                <label className="text-[10px] text-white/30 tech-mono mb-1 block">TEAM B</label>
                                {teams.length > 0 ? (
                                    <select value={teamB} onChange={e => setTeamB(e.target.value)}
                                        className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-500/30 focus:outline-none"
                                        data-testid="select-team-b">
                                        <option value="">Select team...</option>
                                        {teams.filter(t => t !== teamA).map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                ) : (
                                    <Input value={teamB} onChange={e => setTeamB(e.target.value)}
                                        placeholder="e.g. MI" className="bg-black/50 border-white/10 text-white" data-testid="input-team-b" />
                                )}
                            </div>
                            <div>
                                <label className="text-[10px] text-white/30 tech-mono mb-1 block">VENUE</label>
                                <Input value={venue} onChange={e => setVenue(e.target.value)}
                                    placeholder="e.g. Wankhede Stadium" className="bg-black/50 border-white/10 text-white" data-testid="input-venue" />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-[10px] text-white/30 tech-mono mb-1 block">WIN% TEAM A</label>
                                    <Input type="number" min="0" max="100" value={winPctA}
                                        onChange={e => { setWinPctA(e.target.value); setWinPctB(String(100 - (parseFloat(e.target.value) || 0))); }}
                                        className="bg-black/50 border-white/10 text-white" data-testid="input-win-a" />
                                </div>
                                <div>
                                    <label className="text-[10px] text-white/30 tech-mono mb-1 block">WIN% TEAM B</label>
                                    <Input type="number" min="0" max="100" value={winPctB}
                                        onChange={e => { setWinPctB(e.target.value); setWinPctA(String(100 - (parseFloat(e.target.value) || 0))); }}
                                        className="bg-black/50 border-white/10 text-white" data-testid="input-win-b" />
                                </div>
                            </div>
                            <div>
                                <label className="text-[10px] text-white/30 tech-mono mb-1 block">AI REASONING (OPTIONAL)</label>
                                <Input value={reasoning} onChange={e => setReasoning(e.target.value)}
                                    placeholder="e.g. Strong batting lineup advantage at this venue..."
                                    className="bg-black/50 border-white/10 text-white" data-testid="input-reasoning" />
                            </div>
                        </div>
                        <div className="mt-3">
                            <label className="text-[10px] text-white/30 tech-mono mb-1 block">GOD MODE VERDICT (OPTIONAL)</label>
                            <Input value={godModeVerdict} onChange={e => setGodModeVerdict(e.target.value)}
                                placeholder="e.g. 14-engine consensus favors Team A with 72% agreement..."
                                className="bg-black/50 border-white/10 text-white" data-testid="input-god-mode" />
                        </div>

                        <div className="mt-4 flex justify-end gap-2">
                            <Button onClick={handleGenerateImage} disabled={!teamA || !teamB || generatingImage}
                                variant="outline" className="border-white/10 text-white hover:bg-white/5"
                                data-testid="generate-image-btn">
                                {generatingImage ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating Image...</>
                                ) : (
                                    <><ImageIcon className="w-4 h-4 mr-2" /> Generate Image</>
                                )}
                            </Button>
                            <Button onClick={handleGenerate} disabled={!teamA || !teamB || generating}
                                className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white btn-glow"
                                data-testid="generate-btn">
                                {generating ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                                ) : (
                                    <><Zap className="w-4 h-4 mr-2" /> Generate AI Content</>
                                )}
                            </Button>
                        </div>

                        {/* Generated Image Preview */}
                        {generatedImage && (
                            <div className="mt-4 glass-card p-4 border border-cyan-500/20" data-testid="generated-image">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs text-white/30 tech-mono">AI-GENERATED PREDICTION IMAGE</span>
                                    <a href={`data:image/png;base64,${generatedImage}`} download={`${teamA}_vs_${teamB}_prediction.png`}
                                        className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors">
                                        Download
                                    </a>
                                </div>
                                <img src={`data:image/png;base64,${generatedImage}`} alt="Prediction"
                                    className="w-full max-w-md mx-auto rounded-lg border border-white/10" />
                            </div>
                        )}
                    </div>

                    {/* Generated Content */}
                    {Object.keys(contentMap).length > 0 && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h2 className="heading-primary text-sm text-white">GENERATED CONTENT</h2>
                                <span className="text-[10px] text-white/20 tech-mono">EDIT BEFORE POSTING</span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {Object.entries(contentMap).map(([platform, content]) => (
                                    <ContentEditor
                                        key={platform}
                                        platform={platform}
                                        content={content}
                                        onChange={handleContentChange}
                                        onCopy={handleCopy}
                                    />
                                ))}
                            </div>

                            {/* Platform Selection & Post */}
                            <div className="glass-card p-5 border border-white/5">
                                <h3 className="heading-primary text-xs text-white mb-3">SELECT PLATFORMS TO POST</h3>
                                <div className="flex flex-wrap gap-2 mb-4">
                                    {platforms.map(p => {
                                        const meta = PLATFORM_META[p.platform];
                                        const isSelected = selectedPlatforms.includes(p.platform);
                                        return (
                                            <button key={p.platform}
                                                onClick={() => togglePlatform(p.platform)}
                                                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all border ${isSelected
                                                    ? 'border-cyan-500/30 bg-cyan-500/10 text-white'
                                                    : p.connected
                                                        ? 'border-white/10 bg-white/5 text-white/50 hover:border-white/20'
                                                        : 'border-white/5 bg-white/2 text-white/20 cursor-not-allowed'
                                                    }`}
                                                disabled={!p.connected}
                                                data-testid={`toggle-${p.platform}`}>
                                                <PlatformBadge platform={p.platform} size="sm" />
                                                {meta?.label}
                                                {!p.connected && <AlertTriangle className="w-3 h-3 text-amber-400/60" />}
                                                {isSelected && <CheckCircle className="w-3 h-3 text-cyan-400" />}
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="text-[10px] text-white/20">
                                        {selectedPlatforms.length === 0
                                            ? connectedCount === 0
                                                ? 'Configure API keys in Settings > API Configuration to enable posting'
                                                : 'Select at least one platform to post'
                                            : `${selectedPlatforms.length} platform(s) selected`}
                                    </div>
                                    <Button onClick={handlePost}
                                        disabled={selectedPlatforms.length === 0 || posting}
                                        className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white"
                                        data-testid="post-btn">
                                        {posting ? (
                                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Posting...</>
                                        ) : (
                                            <><Send className="w-4 h-4 mr-2" /> Post to {selectedPlatforms.length} Platform(s)</>
                                        )}
                                    </Button>
                                </div>
                            </div>

                            {/* Post Results */}
                            {postResults && (
                                <div className="glass-card p-5 border border-white/5">
                                    <h3 className="heading-primary text-xs text-white mb-3">POST RESULTS</h3>
                                    <div className="space-y-2">
                                        {Object.entries(postResults).map(([platform, result]) => (
                                            <div key={platform} className={`flex items-center justify-between p-3 rounded-lg ${result.success ? 'bg-green-500/10 border border-green-500/20' : 'bg-red-500/10 border border-red-500/20'}`}
                                                data-testid={`result-${platform}`}>
                                                <div className="flex items-center gap-2">
                                                    <PlatformBadge platform={platform} size="sm" />
                                                    <span className="text-sm text-white">{PLATFORM_META[platform]?.label}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {result.success ? (
                                                        <span className="text-xs text-green-400 tech-mono">SENT</span>
                                                    ) : (
                                                        <span className="text-xs text-red-400">{result.error || 'Failed'}</span>
                                                    )}
                                                    {result.success ? (
                                                        <CheckCircle className="w-4 h-4 text-green-400" />
                                                    ) : (
                                                        <XCircle className="w-4 h-4 text-red-400" />
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* HISTORY TAB */}
            {tab === 'history' && (
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <h2 className="heading-primary text-sm text-white">POST HISTORY</h2>
                        <span className="text-[10px] text-white/20 tech-mono">{history.length} POSTS</span>
                    </div>
                    {history.length === 0 ? (
                        <div className="glass-card p-12 text-center">
                            <Clock className="w-8 h-8 text-white/10 mx-auto mb-2" />
                            <div className="text-sm text-white/30">No posts yet</div>
                            <div className="text-xs text-white/15 mt-1">Generate and post content to see history here</div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {history.map(post => (
                                <PostHistoryItem key={post.id} post={post} onDelete={handleDeleteHistory} />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* SETTINGS TAB */}
            {tab === 'settings' && (
                <div className="space-y-4">
                    <div className="glass-card p-5 border border-white/5">
                        <h2 className="heading-primary text-sm text-white mb-4">AUTO-POST SETTINGS</h2>
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-sm text-white">Auto-Post Predictions</div>
                                    <div className="text-xs text-white/30">Automatically post when a new AI prediction is generated</div>
                                </div>
                                <button onClick={() => setSettings(s => ({ ...s, enabled: !s.enabled }))}
                                    className={`w-12 h-6 rounded-full transition-all ${settings.enabled ? 'bg-cyan-500' : 'bg-white/10'}`}
                                    data-testid="auto-post-toggle">
                                    <div className={`w-5 h-5 rounded-full bg-white transition-transform ${settings.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                                </button>
                            </div>
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-sm text-white">AI Content Generation</div>
                                    <div className="text-xs text-white/30">Use GPT-5.2 to generate platform-optimized content</div>
                                </div>
                                <button onClick={() => setSettings(s => ({ ...s, auto_generate: !s.auto_generate }))}
                                    className={`w-12 h-6 rounded-full transition-all ${settings.auto_generate ? 'bg-cyan-500' : 'bg-white/10'}`}
                                    data-testid="auto-generate-toggle">
                                    <div className={`w-5 h-5 rounded-full bg-white transition-transform ${settings.auto_generate ? 'translate-x-6' : 'translate-x-0.5'}`} />
                                </button>
                            </div>

                            <div>
                                <div className="text-sm text-white mb-2">Enabled Platforms</div>
                                <div className="flex flex-wrap gap-2">
                                    {Object.entries(PLATFORM_META).map(([key, meta]) => {
                                        const enabled = settings.platforms?.includes(key);
                                        return (
                                            <button key={key}
                                                onClick={() => setSettings(s => ({
                                                    ...s,
                                                    platforms: enabled
                                                        ? s.platforms.filter(p => p !== key)
                                                        : [...(s.platforms || []), key],
                                                }))}
                                                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs border transition-all ${enabled
                                                    ? 'border-cyan-500/30 bg-cyan-500/10 text-white'
                                                    : 'border-white/10 bg-white/5 text-white/40'}`}
                                                data-testid={`settings-platform-${key}`}>
                                                <PlatformBadge platform={key} size="sm" />
                                                {meta.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="flex justify-end">
                                <Button onClick={handleSaveSettings}
                                    disabled={savingSettings}
                                    className="bg-cyan-600 hover:bg-cyan-500 text-white"
                                    data-testid="save-settings-btn">
                                    {savingSettings ? (
                                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                                    ) : 'Save Settings'}
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* Platform Credentials Management */}
                    <div className="glass-card p-5 border border-white/5">
                        {/* Apify Configuration — Primary Engine */}
                        <div className="mb-4 p-4 rounded-xl border border-purple-500/20 bg-gradient-to-r from-purple-500/5 to-cyan-500/5">
                            <div className="flex items-center gap-2 mb-2">
                                <Zap className="w-4 h-4 text-purple-400" />
                                <span className="heading-primary text-xs text-white">APIFY — PRIMARY SCRAPING ENGINE</span>
                            </div>
                            <div className="text-[10px] text-white/30 mb-3">
                                One Apify token powers all scraping (Twitter, Instagram, YouTube, Facebook, Web News) without needing individual platform API keys.
                                Get your token from <a href="https://console.apify.com/account/integrations" target="_blank" rel="noopener noreferrer" className="text-cyan-400 underline">console.apify.com/account/integrations</a>
                            </div>
                            <CredentialForm
                                platform={{ platform: 'apify', fields: ['api_token'], labels: { api_token: 'Apify API Token' }, configured: false }}
                                onSaved={fetchAll}
                            />
                        </div>

                        <div className="flex items-center justify-between cursor-pointer"
                            onClick={() => setSettingsExpanded(!settingsExpanded)}>
                            <h2 className="heading-primary text-sm text-white">PLATFORM CREDENTIALS (OPTIONAL)</h2>
                            {settingsExpanded ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
                        </div>
                        {settingsExpanded && (
                            <div className="mt-4 space-y-3">
                                {platforms.filter(p => p.platform !== 'apify').map(p => (
                                    <CredentialForm key={p.platform} platform={p} onSaved={fetchAll} />
                                ))}
                            </div>
                        )}
                        {!settingsExpanded && (
                            <div className="text-xs text-white/20 mt-2">
                                Only needed for posting. Apify handles all scraping.
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}


function CredentialForm({ platform, onSaved }) {
    const [fields, setFields] = useState({});
    const [saving, setSaving] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [platformFields, setPlatformFields] = useState(null);

    useEffect(() => {
        API.get('/social/platform-fields').then(res => {
            setPlatformFields(res.data.platforms?.[platform.platform] || null);
        }).catch(() => {});
    }, [platform.platform]);

    if (!platformFields) return null;

    const meta = PLATFORM_META[platform.platform];

    const handleSave = async () => {
        setSaving(true);
        try {
            await API.post('/social/save-credentials', {
                service: platform.platform,
                config: fields,
            });
            toast.success(`${meta?.label || platform.platform} credentials saved`);
            setExpanded(false);
            setFields({});
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save credentials');
        }
        setSaving(false);
    };

    return (
        <div className={`rounded-lg border p-4 ${platform.connected ? 'border-green-500/20 bg-green-500/5' : 'border-white/5 bg-white/2'}`}
            data-testid={`cred-${platform.platform}`}>
            <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
                <div className="flex items-center gap-2">
                    <PlatformBadge platform={platform.platform} size="sm" />
                    <span className="text-sm font-bold text-white">{meta?.label}</span>
                    {platform.connected && <CheckCircle className="w-3.5 h-3.5 text-green-400" />}
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-[10px] text-white/20 tech-mono">
                        {platform.connected ? 'CONFIGURED' : 'NOT SET'}
                    </span>
                    {expanded ? <ChevronUp className="w-3.5 h-3.5 text-white/30" /> : <ChevronDown className="w-3.5 h-3.5 text-white/30" />}
                </div>
            </div>
            {expanded && (
                <div className="mt-3 space-y-2">
                    <div className="text-[10px] text-white/20 mb-2">{platformFields.help}</div>
                    {platformFields.fields.map(field => (
                        <div key={field}>
                            <label className="text-[10px] text-white/30 tech-mono mb-0.5 block">
                                {platformFields.labels[field] || field}
                            </label>
                            <Input
                                type="password"
                                placeholder={`Enter ${platformFields.labels[field] || field}...`}
                                value={fields[field] || ''}
                                onChange={e => setFields(prev => ({ ...prev, [field]: e.target.value }))}
                                className="bg-black/50 border-white/10 text-white"
                                data-testid={`cred-input-${platform.platform}-${field}`}
                            />
                        </div>
                    ))}
                    <div className="flex justify-end gap-2 mt-2">
                        <Button variant="ghost" size="sm" onClick={() => { setExpanded(false); setFields({}); }}
                            className="text-white/40 hover:text-white text-xs">
                            Cancel
                        </Button>
                        <Button size="sm" onClick={handleSave} disabled={saving}
                            className="bg-green-600 hover:bg-green-700 text-white text-xs"
                            data-testid={`cred-save-${platform.platform}`}>
                            {saving ? 'Saving...' : 'Save Credentials'}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
